import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, MousePointerClick, Gauge, Smartphone, RefreshCw, Globe } from 'lucide-react';

const WhyHTML5Section: React.FC = () => {
  const navigate = useNavigate();

  return (
    <section className="py-24 lg:py-32 relative overflow-hidden">
      {/* Dark background */}
      <div className="absolute inset-0 bg-slate-900 dark:bg-[#0a0a0f]">
        <div className="absolute inset-0 grid-pattern opacity-50" />
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] opacity-30">
          <div className="w-full h-full bg-gradient-to-br from-[#635bff] to-transparent rounded-full blur-3xl" />
        </div>
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] opacity-20">
          <div className="w-full h-full bg-gradient-to-tl from-[#00d4ff] to-transparent rounded-full blur-3xl" />
        </div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-flex items-center px-4 py-2 rounded-full bg-[#635bff]/20 border border-[#635bff]/30 text-sm font-semibold text-[#a78bfa] mb-6">
            Why HTML5?
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white leading-tight">
            Why HTML5 Rich Media Ads{' '}
            <span className="stripe-gradient-text">over Static Ads?</span>
          </h2>
          <p className="mt-6 text-lg text-slate-400 leading-relaxed">
            Advertisers are choosing HTML5 ads over static image files (like GIF/JPG) primarily for higher engagement, better performance, and versatility. HTML5 ads offer rich media capabilities — including animations, video, and interactivity — that drive significantly higher click-through rates and overcome static banner ad fatigue.
          </p>
        </div>

        {/* Big Stat Callout */}
        <div className="flex justify-center mb-16">
          <div className="relative group">
            <div className="absolute -inset-1 bg-gradient-to-r from-[#635bff] via-[#7c3aed] to-[#00d4ff] rounded-2xl opacity-60 blur-lg group-hover:opacity-80 transition-opacity" />
            <div className="relative bg-slate-800/90 backdrop-blur-sm border border-white/10 rounded-2xl px-12 py-10 text-center">
              <div className="flex items-baseline justify-center gap-2">
                <span className="text-6xl sm:text-7xl lg:text-8xl font-extrabold stripe-gradient-text">250%</span>
              </div>
              <p className="mt-3 text-lg text-slate-300 font-medium">Higher Click-Through Rate</p>
              <p className="mt-1 text-sm text-slate-500">HTML5 rich media ads vs. static banner ads</p>
            </div>
          </div>
        </div>

        {/* Benefits Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {/* Benefit 1: High Engagement */}
          <div className="group relative bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:bg-white/10 hover:border-white/20 transition-all duration-300">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-gradient-to-br from-[#635bff] to-purple-500 mb-6 shadow-lg">
              <MousePointerClick className="w-7 h-7 text-white" />
            </div>
            <h3 className="text-xl font-bold text-white mb-3">High Engagement & Rich Media</h3>
            <p className="text-slate-400 leading-relaxed">
              Support animations, video, 3D elements, and interactive, clickable components that attract more attention and drive deeper user engagement.
            </p>
            <div className="absolute -inset-px bg-gradient-to-r from-[#635bff] to-purple-500 rounded-2xl opacity-0 group-hover:opacity-10 transition-opacity duration-300 -z-10 blur-xl" />
          </div>

          {/* Benefit 2: Smaller File Sizes */}
          <div className="group relative bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:bg-white/10 hover:border-white/20 transition-all duration-300">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-500 mb-6 shadow-lg">
              <Gauge className="w-7 h-7 text-white" />
            </div>
            <h3 className="text-xl font-bold text-white mb-3">Smaller File Sizes & Fast Loading</h3>
            <p className="text-slate-400 leading-relaxed">
              Despite being richer, HTML5 ads are often more efficient than complex animated GIFs, leading to faster loading times and better user experience.
            </p>
            <div className="absolute -inset-px bg-gradient-to-r from-cyan-500 to-blue-500 rounded-2xl opacity-0 group-hover:opacity-10 transition-opacity duration-300 -z-10 blur-xl" />
          </div>

          {/* Benefit 3: Responsiveness */}
          <div className="group relative bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:bg-white/10 hover:border-white/20 transition-all duration-300">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 mb-6 shadow-lg">
              <Smartphone className="w-7 h-7 text-white" />
            </div>
            <h3 className="text-xl font-bold text-white mb-3">Responsiveness & Scalability</h3>
            <p className="text-slate-400 leading-relaxed">
              A single HTML5 ad can scale to different screen sizes — mobile, desktop, and tablet — without losing quality or requiring separate creative builds.
            </p>
            <div className="absolute -inset-px bg-gradient-to-r from-emerald-500 to-teal-500 rounded-2xl opacity-0 group-hover:opacity-10 transition-opacity duration-300 -z-10 blur-xl" />
          </div>

          {/* Benefit 4: Dynamic Functionality */}
          <div className="group relative bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:bg-white/10 hover:border-white/20 transition-all duration-300 md:col-span-1 lg:col-span-1">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 mb-6 shadow-lg">
              <RefreshCw className="w-7 h-7 text-white" />
            </div>
            <h3 className="text-xl font-bold text-white mb-3">Dynamic Functionality</h3>
            <p className="text-slate-400 leading-relaxed">
              Change content in real-time based on the user's location, time of day, or device — enabling hyper-personalized ad experiences at scale.
            </p>
            <div className="absolute -inset-px bg-gradient-to-r from-amber-500 to-orange-500 rounded-2xl opacity-0 group-hover:opacity-10 transition-opacity duration-300 -z-10 blur-xl" />
          </div>

          {/* Benefit 5: Broad Compatibility */}
          <div className="group relative bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:bg-white/10 hover:border-white/20 transition-all duration-300 md:col-span-1 lg:col-span-2">
            <div className="flex flex-col sm:flex-row sm:items-start gap-6">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-gradient-to-br from-rose-500 to-pink-500 shadow-lg flex-shrink-0">
                <Globe className="w-7 h-7 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white mb-3">Broad Compatibility</h3>
                <p className="text-slate-400 leading-relaxed">
                  HTML5 ads work across all modern browsers and apps, unlike older, insecure technologies like Flash. This ensures your campaigns reach every audience, everywhere — with no plugin requirements and full security compliance.
                </p>
              </div>
            </div>
            <div className="absolute -inset-px bg-gradient-to-r from-rose-500 to-pink-500 rounded-2xl opacity-0 group-hover:opacity-10 transition-opacity duration-300 -z-10 blur-xl" />
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16">
          <button
            onClick={() => navigate('/platform')}
            className="inline-flex items-center px-8 py-4 text-base font-semibold text-slate-900 bg-white rounded-full hover:bg-slate-100 transition-all hover:shadow-xl group"
          >
            Explore the HTML5 Ad Builder
            <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </div>
    </section>
  );
};

export default WhyHTML5Section;
